import React, { useState, useRef, useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import gsap from "gsap";
import Header from "../components/Header/Header";
import Navbar from "../components/Navbar/Navbar";
import VistaAI from "../components/VistaAI/VistaAI";
import "./AppShell.scss";
import "./AppShellDashboard.scss";

export function getUploadStageVars(width, height) {
  const viewportWidth = Number.isFinite(width) && width > 0 ? width : 1440;
  const viewportHeight = Number.isFinite(height) && height > 0 ? height : 1024;
  const scale = Math.min(1, viewportWidth / 1440, viewportHeight / 1024);

  return {
    "--sv-upload-stage-scale": String(scale),
  };
}

export default function AppShell() {
  const [vistaOpen, setVistaOpen] = useState(false);
  const routeLocation = useLocation();
  const contentRef = useRef(null);
  const isLobby = routeLocation.pathname === "/lobby";
  const isNotifications = routeLocation.pathname === "/notifications";
  const routeKey = routeLocation.pathname === "/"
    ? "home"
    : routeLocation.pathname.split("/")[1] || "default";
  const isUpload = routeKey === "upload";
  const [uploadStageVars, setUploadStageVars] = useState(() =>
    typeof window === "undefined"
      ? getUploadStageVars(1440, 1024)
      : getUploadStageVars(window.innerWidth, window.innerHeight)
  );
  const notificationShellVars = isNotifications
    ? {
        "--sv-shell-edge-offset": "clamp(18px, 2.8vw, 40px)",
        "--sv-shell-top-offset": "clamp(18px, 2.6vw, 30px)",
        "--sv-bottom-dock-space": "clamp(4.6rem, 9vw, 5.75rem)",
      }
    : undefined;
  const shellVars = notificationShellVars || isUpload
    ? { ...notificationShellVars, ...(isUpload ? uploadStageVars : undefined) }
    : undefined;

  useEffect(() => {
    if (!isUpload) {
      return undefined;
    }

    function updateUploadStageVars() {
      setUploadStageVars(getUploadStageVars(window.innerWidth, window.innerHeight));
    }

    updateUploadStageVars();
    window.addEventListener("resize", updateUploadStageVars);
    return () => window.removeEventListener("resize", updateUploadStageVars);
  }, [isUpload]);

  useEffect(() => {
    if (contentRef.current) {
      gsap.fromTo(contentRef.current,
        { opacity: 0, y: 12 },
        { opacity: 1, y: 0, duration: 0.35, ease: 'power2.out' }
      );
    }
  }, [routeLocation.pathname]);

  useEffect(() => {
    const params = new URLSearchParams(routeLocation.search);
    if (params.has("vistaState") || params.has("vista")) {
      setVistaOpen(true);
    }
  }, [routeLocation.search]);

  return (
    <div className={`app-shell app-shell--${routeKey}`} style={shellVars}>
      <div className="app-shell__glow app-shell__glow--primary" aria-hidden="true" />
      <div className="app-shell__glow app-shell__glow--secondary" aria-hidden="true" />
      <Header isNotifications={isNotifications} />
      <main className="app-shell__content" ref={contentRef}>
        <Outlet context={{ vistaOpen, setVistaOpen }} />
      </main>
      <Navbar
        isLobby={isLobby}
        isNotifications={isNotifications}
        isVistaOpen={vistaOpen}
        onVistaToggle={() => setVistaOpen(true)}
      />

      {/* Global Vista AI panel */}
      {vistaOpen && (
        <VistaAI onClose={() => setVistaOpen(false)} />
      )}
    </div>
  );
}
